<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">{{ editing ? "Editar entidad pensionaria" : "Nueva entidad pensionaria" }}</h1>
    </div>

    <form @submit.prevent="save" novalidate>
      <wz-card :title="editing ? 'Datos de la entidad' : 'Datos de la nueva entidad'">
        <div class="flex flex-wrap -mx-2">

          <!-- Siempre visibles -->
          <div class="w-full md:w-2/3 mb-3 px-2">
            <wz-input label="Nombre" required v-model="form.name" placeholder="Nombre de la entidad"
              :error="$v.form.name.$error && !$v.form.name.required ? 'El nombre es requerido' : ''" />
          </div>
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-input label="Código" required v-model="form.code" placeholder="Ej: AFP01" :disabled="editing"
              :error="$v.form.code.$error && !$v.form.code.required ? 'El código es requerido' : ''" />
          </div>
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-select label="Tipo de entidad" required :items="typeOptions" v-model="form.type"
              defaultOption="Seleccione tipo" :disabled="editing"
              :error="$v.form.type.$error && !$v.form.type.required ? 'El tipo es requerido' : ''" />
          </div>

          <!-- ONP -->
          <div class="w-full md:w-1/3 mb-3 px-2" v-if="form.type === 'ONP'">
            <wz-input label="Tasa de aporte (%)" required type="number" v-model.number="form.mandatoryContributionRate"
              placeholder="13.00" step="0.0001" min="0" max="100" suffix="%"
              :error="$v.form.mandatoryContributionRate.$error
                ? (!$v.form.mandatoryContributionRate.required ? 'La tasa de aporte es requerida'
                  : !$v.form.mandatoryContributionRate.between ? 'Debe ser un valor entre 0 y 100' : '')
                : ''" />
          </div>

          <!-- AFP -->
          <template v-if="form.type === 'AFP'">
            <div class="w-full md:w-1/3 mb-3 px-2">
              <wz-input label="Aporte obligatorio (%)" required type="number" v-model.number="form.mandatoryContributionRate"
                placeholder="10.00" step="0.0001" min="0" max="100" suffix="%"
                :error="$v.form.mandatoryContributionRate.$error
                  ? (!$v.form.mandatoryContributionRate.required ? 'El aporte obligatorio es requerido'
                    : !$v.form.mandatoryContributionRate.between ? 'Debe ser un valor entre 0 y 100' : '')
                  : ''" />
            </div>
            <div class="w-full md:w-1/3 mb-3 px-2">
              <wz-input label="Prima de seguro (%)" required type="number" v-model.number="form.insurancePremiumRate"
                placeholder="1.37" step="0.0001" min="0" max="100" suffix="%"
                :error="$v.form.insurancePremiumRate.$error
                  ? (!$v.form.insurancePremiumRate.required ? 'La prima de seguro es requerida para AFP'
                    : !$v.form.insurancePremiumRate.between ? 'Debe ser un valor entre 0 y 100' : '')
                  : ''" />
            </div>
            <div class="w-full md:w-1/3 mb-3 px-2">
              <wz-select label="Modalidad de comisión" required :items="commissionTypeOptions" v-model="form.commissionType"
                defaultOption="Seleccione modalidad"
                :error="$v.form.commissionType.$error && !$v.form.commissionType.required ? 'La modalidad es requerida' : ''" />
            </div>

            <!-- Modalidad Flujo -->
            <div class="w-full md:w-1/3 mb-3 px-2" v-if="form.commissionType === 'FLUJO'">
              <wz-input label="Comisión sobre remuneración (%)" required type="number" v-model.number="form.flowCommissionRate"
                placeholder="Ej: 1.47" step="0.0001" min="0" max="100" suffix="%"
                :error="$v.form.flowCommissionRate.$error
                  ? (!$v.form.flowCommissionRate.required ? 'La comisión sobre remuneración es requerida'
                    : !$v.form.flowCommissionRate.between ? 'Debe ser un valor entre 0 y 100' : '')
                  : ''" />
            </div>

            <!-- Modalidad Mixta -->
            <!-- Campo "Comisión sobre saldo (% anual)" (mixedCommissionRate) OCULTO a pedido de RRHH:
                 no se descuenta en planilla (la AFP la cobra del fondo). El dato se conserva en BD y en
                 `form` (se recarga al editar y se reenvía en el submit). Reversible: restaurar este bloque. -->
          </template>
        </div>

        <div class="flex justify-end gap-2 mt-2">
          <wz-button variant="light" @click="$router.push({ name: 'pension-entity-list' })">{{ editing ? "Volver" : "Cancelar" }}</wz-button>
          <wz-button type="submit" variant="primary" class="ml-2">Guardar</wz-button>
        </div>
      </wz-card>
    </form>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { required, requiredIf, between } from 'vuelidate/lib/validators'
import { PensionEntityService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

export default {
  data() {
    return {
      editing: false,
      isLoading: false,
      typeOptions: Object.values(LocalConstants.PensionEntityType).map(t => ({ key: t.key, label: t.label })),
      commissionTypeOptions: [
        { key: 'FLUJO',       label: 'Por flujo' },
        { key: 'MIXTA', label: 'Mixta' },
      ],
      form: {
        name: null, code: null, type: null, commissionType: null,
        mandatoryContributionRate: null, insurancePremiumRate: null,
        flowCommissionRate: null, mixedCommissionRate: null,
      },
    }
  },
  validations() {
    return {
      form: {
        name: { required },
        code: { required },
        type: { required },
        mandatoryContributionRate: { required, between: between(0, 100) },
        insurancePremiumRate: { required: requiredIf(() => this.form.type === 'AFP'), between: between(0, 100) },
        commissionType: { required: requiredIf(() => this.form.type === 'AFP') },
        flowCommissionRate: {
          required: requiredIf(() => this.form.type === 'AFP' && this.form.commissionType === 'FLUJO'),
          between: between(0, 100),
        },
        // mixedCommissionRate: campo oculto (ver template). Sin validación en el form; el valor se conserva
        // en `form` (recargado desde BD al editar) y se reenvía tal cual en el submit.
      },
    }
  },
  watch: {
    '$route.params.id'() { if (this.$route.params.id) this.load(this.$route.params.id) },
    'form.type'(newVal, oldVal) {
      if (this.isLoading) return
      if (newVal === 'ONP') {
        this.form.mandatoryContributionRate = 13.00
        this.form.commissionType = null
        this.form.insurancePremiumRate = null
        this.form.flowCommissionRate = null
        this.form.mixedCommissionRate = null
      } else if (newVal === 'AFP') {
        // Al venir de ONP, resetear el aporte; en creación precargar defaults.
        if (oldVal === 'ONP') this.form.mandatoryContributionRate = null
        if (this.form.mandatoryContributionRate == null) this.form.mandatoryContributionRate = 10.00
        if (this.form.insurancePremiumRate == null) this.form.insurancePremiumRate = 1.37
      }
    },
    'form.commissionType'(newVal) {
      if (this.isLoading) return
      if (newVal === 'FLUJO') this.form.mixedCommissionRate = null
      else if (newVal === 'MIXTA') this.form.flowCommissionRate = null
    },
  },
  methods: {
    async save() {
      this.$v.$touch()
      if (this.$v.$error) return
      try {
        this.$store.dispatch('app/loading', true)
        const isAfp = this.form.type === 'AFP'
        const pct = v => v != null ? v / 100 : null
        const data = {
          name: this.form.name,
          code: this.form.code,
          type: this.form.type,
          commissionType: isAfp ? this.form.commissionType : null,
          mandatoryContributionRate: pct(this.form.mandatoryContributionRate),
          insurancePremiumRate: isAfp ? pct(this.form.insurancePremiumRate) : null,
          flowCommissionRate: isAfp && this.form.commissionType === 'FLUJO' ? pct(this.form.flowCommissionRate) : null,
          mixedCommissionRate: isAfp && this.form.commissionType === 'MIXTA' ? pct(this.form.mixedCommissionRate) : null,
        }
        let response
        if (!this.editing) response = await PensionEntityService.save(data)
        else response = await PensionEntityService.update(this.$route.params.id, data)
        Alert.success('Se almacenó la información correctamente')
        if (!this.editing) { this.$router.push({ name: 'pension-entity-edit', params: { id: response.payload.id } }); this.editing = true }
      } catch (ex) { console.error(ex); Alert.error(ex.errors ? ex.errors.message : 'Error al guardar') }
      finally { this.$store.dispatch('app/loading', false) }
    },
    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        this.isLoading = true
        const p = (await PensionEntityService.get(id)).payload
        const toPct = v => v != null ? parseFloat((v * 100).toFixed(4)) : null
        // Fallback legacy: AFP sin commissionType → inferir por la tasa presente.
        let commissionType = p.commissionType
        if (p.type === 'AFP' && !commissionType) {
          if (p.flowCommissionRate != null) commissionType = 'FLUJO'
          else if (p.mixedCommissionRate != null) commissionType = 'MIXTA'
        }
        this.form = {
          name: p.name, code: p.code, type: p.type, commissionType,
          mandatoryContributionRate: toPct(p.mandatoryContributionRate),
          insurancePremiumRate: toPct(p.insurancePremiumRate),
          flowCommissionRate: toPct(p.flowCommissionRate),
          mixedCommissionRate: toPct(p.mixedCommissionRate),
        }
        this.editing = true
        this.$nextTick(() => { this.isLoading = false })
      } catch (e) {
        console.error(e); Alert.error(e.errors ? e.errors.message : 'Error al cargar'); this.$router.push({ name: 'pension-entity-list' })
      } finally { this.$store.dispatch('app/loading', false) }
    },
  },
  async created() { if (this.$route.params.id) this.load(this.$route.params.id) },
}
</script>
